import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface Slide {
  /** Tab label, e.g. "Ladder logic". */
  label: string;
  /** One sentence shown under the picture. */
  caption: string;
  /** Path under public/. */
  src: string;
}

/**
 * Real screenshots, one at a time, in a browser frame. Tabs name every slide
 * so a visitor can jump to the one they care about rather than click through.
 */
export function ScreenshotSlider({ slides, color = '#3b82f6' }: { slides: Slide[]; color?: string }) {
  const [index, setIndex] = useState(0);
  const go = (i: number) => setIndex((i + slides.length) % slides.length);
  const slide = slides[index];

  return (
    <div
      className="outline-none"
      tabIndex={0}
      aria-roledescription="carousel"
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') go(index - 1);
        if (e.key === 'ArrowRight') go(index + 1);
      }}
    >
      <div className="flex flex-wrap justify-center gap-2 mb-5" role="tablist">
        {slides.map((s, i) => (
          <button
            key={s.label}
            type="button"
            role="tab"
            aria-selected={i === index}
            onClick={() => go(i)}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
              i === index ? 'text-white border-transparent' : 'bg-white border-glass-border text-text-secondary hover:text-text-primary'
            }`}
            style={i === index ? { backgroundColor: color } : undefined}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="relative">
        <div className="rounded-[var(--radius-glass)] border border-glass-border bg-white shadow-xl overflow-hidden">
          <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-glass-border bg-ink-muted/60">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
            <span className="ml-3 text-xs text-text-muted truncate">{slide.label}</span>
          </div>
          {/* Every slide is mounted so switching is instant; only the current one is shown. */}
          <div className="relative aspect-[16/10] bg-ink-base">
            {slides.map((s, i) => (
              <img
                key={s.src}
                src={s.src}
                alt={`${s.label} in Scada.Ink`}
                loading={i === 0 ? 'eager' : 'lazy'}
                className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-300 ${i === index ? 'opacity-100' : 'opacity-0'}`}
                aria-hidden={i !== index}
              />
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="Previous screenshot"
          className="hidden sm:flex absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-glass-border shadow-lg items-center justify-center text-text-secondary hover:text-text-primary"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="Next screenshot"
          className="hidden sm:flex absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-glass-border shadow-lg items-center justify-center text-text-secondary hover:text-text-primary"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="mt-5 flex items-center justify-between gap-4">
        <p className="text-sm text-text-secondary leading-relaxed">{slide.caption}</p>
        <div className="flex sm:hidden gap-2 shrink-0">
          <button type="button" onClick={() => go(index - 1)} aria-label="Previous screenshot" className="w-9 h-9 rounded-full border border-glass-border bg-white flex items-center justify-center">
            <ChevronLeft size={18} />
          </button>
          <button type="button" onClick={() => go(index + 1)} aria-label="Next screenshot" className="w-9 h-9 rounded-full border border-glass-border bg-white flex items-center justify-center">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
